import secureApi from "../../utils/secureApi";

// ---------------------------------------------------------------------------
// Shared types — mirrors GALLERY_FRONTEND_INTEGRATION_GUIDE.md.
// ---------------------------------------------------------------------------

export interface GalleryPlaceholder {
  type: "blurhash" | "thumbhash" | "lqip" | "dominant_color";
  value: string;
  dominantColor: string | null;
  width: number | null;
  height: number | null;
}

export interface RegionPrice {
  amount: number;
  currency: string;
}

export interface GalleryProduct {
  id: string;
  name: string;
  description: string | null;
  type: "one_time" | "subscription" | "bundle" | "trial";
  pricing: Record<string, RegionPrice>;
}

export interface GalleryCategory {
  id: string;
  name: string;
  slug: string | null;
  moduleType: "tournament" | "academy";
  moduleId: string | null;
  tournamentId: string | null;
  seasonId: string | null;
  sport: string | null;
  uniqueId: number;
  thumbnailPortrait: string | null;
  thumbnailLandscape: string | null;
  albumCount: number;
}

export interface GalleryCategoryDetail extends GalleryCategory {
  albums: GalleryAlbum[];
}

export interface GalleryCover {
  id: string;
  type: "image" | "video";
  placeholder: GalleryPlaceholder | null;
  mimeType: string | null;
  dateUploaded: string;
}

export interface GalleryAlbum {
  id: string;
  name: string;
  uniqueId: number;
  thumbnailPortrait: string | null;
  thumbnailLandscape: string | null;
  accessType: "public" | "paid";
  allowDownload: boolean;
  freePreviewMediaIds: string[];
  freePreviewCount: number;
  resourceKey: string;
  owned: boolean;
  product: GalleryProduct | null;
  mediaCount: number;
  cover: GalleryCover | null;
  category: GalleryCategory;
}

/** Upload/processing status returned by the admin listings. Completion is
 * asynchronous: `complete` queues a `gallery-image-processing` SQS job and
 * returns `202`, so an item stays `queued`/`processing` until the worker
 * finishes it (guide §15 "Upload and process an image"). */
export type GalleryProcessingStatus =
  | "pending"
  | "queued"
  | "processing"
  | "ready"
  | "failed";

export interface GalleryMedia {
  id: string;
  type: "image" | "video";
  placeholder: GalleryPlaceholder | null;
  mimeType: string | null;
  size: number | null;
  dateUploaded: string;
  locked: boolean;
  isFreePreview: boolean;
  contentUrl: string | null;
  contentUrlExpiresAt: string | null;
  /** Present on the admin listing while an upload is not yet ready. */
  status?: GalleryProcessingStatus;
  /** Set when `status` is `"failed"` — show this and let the admin retry. */
  processingError?: string;
  processingQueuedAt?: string;
  processedAt?: string;
}

export interface Pagination {
  page: number;
  limit: number;
  total: number;
  totalPages: number;
}

export interface MatchGalleryPhotoAdmin {
  id: string;
  type: "image";
  placeholder: GalleryPlaceholder | null;
  mimeType: string | null;
  size: number | null;
  uploadedAt: string;
  isFree: boolean;
  locked: boolean;
  /** Every `ready` item (Free or Paid) carries an admin-only short-lived
   * signed preview URL regardless of purchase state — this listing does
   * not apply customer purchase checks (guide §20). */
  contentUrl: string | null;
  contentUrlExpiresAt: string | null;
  status?: GalleryProcessingStatus;
  processingError?: string;
}

export interface MatchGalleryAdminPage {
  matchId: string;
  resourceKey: string;
  accessType: "public" | "paid";
  /** Match-level download preference — hide download controls when false. */
  allowDownload: boolean;
  /** Present when the match gallery is paid — the Match Photos Pass price
   * configured for this match. */
  pricing?: Record<string, RegionPrice>;
  freePreviewPhotoIds: string[];
  freePreviewCount: number;
  items: MatchGalleryPhotoAdmin[];
  pagination: Pagination;
}

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const unwrap = <T,>(response: any): T => (response?.data ?? response) as T;
// eslint-disable-next-line @typescript-eslint/no-explicit-any
const errorMessage = (error: any, fallback: string) =>
  error?.response?.data?.error ??
  error?.response?.data?.message ??
  (Array.isArray(error?.response?.data?.details)
    ? error.response.data.details.join(", ")
    : undefined) ??
  error?.message ??
  fallback;

// ---------------------------------------------------------------------------
// Customer read endpoints — admin screens reuse these for listing/detail
// (guide §15: "Admin screens can use the SFL TV customer GET endpoints").
// ---------------------------------------------------------------------------

export async function listGalleryCategories(params: {
  seasonId?: string;
  tournamentId?: string;
  page?: number;
  limit?: number;
}): Promise<{ items: GalleryCategory[]; pagination: Pagination }> {
  try {
    return unwrap(
      await secureApi.get(`/api/v1/sfl-tv/gallery/categories`, { params }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load gallery categories."));
  }
}

export async function getGalleryCategory(
  categoryId: string,
): Promise<GalleryCategoryDetail> {
  try {
    return unwrap(
      await secureApi.get(`/api/v1/sfl-tv/gallery/categories/${categoryId}`),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load the category."));
  }
}

export async function getGalleryAlbumMedia(
  albumId: string,
  page = 1,
  limit = 50,
): Promise<{ album: GalleryAlbum; items: GalleryMedia[]; pagination: Pagination }> {
  try {
    return unwrap(
      await secureApi.get(`/api/v1/sfl-tv/gallery/albums/${albumId}/media`, {
        params: { page, limit },
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load album media."));
  }
}

// ---------------------------------------------------------------------------
// Admin gallery endpoints — `/api/v2/tournaments/gallery/*`. All require
// the `academy-admin` role (enforced server-side from the auth token).
// ---------------------------------------------------------------------------

const ADMIN_ROOT = "/api/v2/tournaments/gallery";

export interface CreateCategoryPayload {
  name: string;
  moduleType: "tournament" | "academy";
  module: string;
  tournament?: string;
  tournamentSeason?: string;
  uniqueId: number;
  thumbnailPortrait?: string;
  thumbnailLandscape?: string;
  sport?: string;
}

export async function createGalleryCategory(
  payload: CreateCategoryPayload,
): Promise<GalleryCategoryDetail> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/categories`, {
        ...payload,
        subcategories: [],
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to create the category."));
  }
}

export interface CreateAlbumPayload {
  name: string;
  uniqueId: number;
  thumbnailPortrait?: string;
  thumbnailLandscape?: string;
}

export async function createGalleryAlbum(
  categoryId: string,
  payload: CreateAlbumPayload,
): Promise<GalleryCategoryDetail> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/categories/${categoryId}/albums`, {
        subcategories: payload,
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to create the album."));
  }
}

export interface ConfigureAlbumPayload {
  accessType: "public" | "paid";
  productId: string | null;
  allowDownload: boolean;
}

export interface ConfigureAlbumResult {
  albumId: string;
  accessType: "public" | "paid";
  productId: string | null;
  allowDownload: boolean;
  freePreviewMediaIds: string[];
  freePreviewCount: number;
}

export async function configureGalleryAlbum(
  albumId: string,
  payload: ConfigureAlbumPayload,
): Promise<ConfigureAlbumResult> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/albums/${albumId}/configure`, payload),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to save album access settings."));
  }
}

/** Admin-only album listing that returns every upload, including
 * `pending`/`queued`/`processing`/`failed` media — the customer media
 * endpoint only ever returns `ready` items, so this is what the admin
 * portal must poll while uploads are still processing (guide §15). */
export async function getAdminAlbumMedia(
  albumId: string,
  page = 1,
  limit = 50,
): Promise<{ items: GalleryMedia[]; pagination: Pagination; album?: GalleryAlbum }> {
  try {
    const data = unwrap<{
      items?: GalleryMedia[];
      pagination?: Pagination;
      album?: GalleryAlbum;
    }>(
      await secureApi.get(`${ADMIN_ROOT}/admin/albums/${albumId}/media`, {
        params: { page, limit },
      }),
    );
    return {
      items: data?.items ?? [],
      pagination:
        data?.pagination ?? { page, limit, total: 0, totalPages: 1 },
      album: data?.album,
    };
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load the album's uploads."));
  }
}

export async function setAlbumPreviews(
  albumId: string,
  mediaIds: string[],
): Promise<{ albumId: string; freePreviewMediaIds: string[]; freePreviewCount: number }> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/albums/${albumId}/previews`, {
        mediaIds,
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to update free previews."));
  }
}

export interface InitiateUploadPayload {
  fileName: string;
  contentType: string;
  category: string;
  subcategory: string;
  module: string;
  tournament?: string;
  tournamentSeason?: string;
  isFreePreview?: boolean;
}

export interface InitiateUploadResult {
  mediaId: string;
  uploadUrl: string;
  expiresIn: number;
  isFreePreview: boolean;
  requiredHeaders: Record<string, string>;
}

export async function initiateGalleryUpload(
  payload: InitiateUploadPayload,
): Promise<InitiateUploadResult> {
  try {
    return unwrap(await secureApi.post(`${ADMIN_ROOT}/uploads/initiate`, payload));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to start the upload."));
  }
}

/** Response to `complete` — the endpoint only verifies the S3 object with
 * `HEAD` and queues processing; it never returns final image metadata
 * synchronously. `status` is `"processing"` on a fresh completion, or
 * `"ready"` if this media was already completed before (idempotent replay
 * — guide §15 "If that media is already ready..."). */
export interface CompleteUploadResponse {
  mediaId: string;
  status: "processing" | "ready";
  isFreePreview?: boolean;
}

export async function completeGalleryUpload(
  mediaId: string,
): Promise<CompleteUploadResponse> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/uploads/${mediaId}/complete`),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to process the uploaded image."));
  }
}

// ---------------------------------------------------------------------------
// Match gallery admin endpoints
// ---------------------------------------------------------------------------

export async function getMatchPhotosAdmin(
  matchId: string,
  page = 1,
  limit = 30,
): Promise<MatchGalleryAdminPage> {
  try {
    return unwrap(
      await secureApi.get(`${ADMIN_ROOT}/matches/${matchId}/photos`, {
        params: { page, limit },
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load match photos."));
  }
}

export interface ConfigureMatchGalleryPayload {
  accessType?: "public" | "paid";
  /** Optional — omitting it preserves the current setting (guide §20). */
  allowDownload?: boolean;
  /** Required when setting accessType to "paid" — the Match Photos Pass
   * price for this match, e.g. { IN: { amount: 39, currency: "INR" } }. */
  pricing?: Record<string, RegionPrice>;
}

export async function configureMatchGallery(
  matchId: string,
  payload: ConfigureMatchGalleryPayload,
): Promise<{
  matchId: string;
  accessType: "public" | "paid";
  allowDownload?: boolean;
  pricing?: Record<string, RegionPrice>;
}> {
  try {
    const body: ConfigureMatchGalleryPayload = {};
    if (payload.accessType !== undefined) body.accessType = payload.accessType;
    if (payload.allowDownload !== undefined) body.allowDownload = payload.allowDownload;
    if (payload.pricing !== undefined) body.pricing = payload.pricing;
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/matches/${matchId}/configure`, body),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to save the match gallery access."));
  }
}

export async function setMatchPreviews(
  matchId: string,
  photoIds: string[],
): Promise<{ matchId: string; freePreviewPhotoIds: string[]; freePreviewCount: number }> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/matches/${matchId}/previews`, {
        photoIds,
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to update free photos."));
  }
}

export interface InitiateMatchUploadFile {
  fileName: string;
  contentType: string;
  isFreePreview?: boolean;
}

export interface InitiateMatchUploadItem {
  photoId: string;
  fileName: string;
  uploadUrl: string;
  expiresIn: number;
  requiredHeaders: Record<string, string>;
  isFreePreview: boolean;
}

export async function initiateMatchUploads(
  matchId: string,
  sport: string,
  files: InitiateMatchUploadFile[],
): Promise<{ matchId: string; items: InitiateMatchUploadItem[] }> {
  try {
    return unwrap(
      await secureApi.post(`${ADMIN_ROOT}/matches/${matchId}/uploads/initiate`, {
        sport,
        files,
      }),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to start the photo upload."));
  }
}

/** Same asynchronous shape as `completeGalleryUpload` — verifies the S3
 * object and queues processing; never returns final metadata synchronously. */
export interface CompleteMatchUploadResponse {
  photoId: string;
  matchId: string;
  status: "processing" | "ready";
  isFree?: boolean;
}

export async function completeMatchUpload(
  matchId: string,
  photoId: string,
): Promise<CompleteMatchUploadResponse> {
  try {
    return unwrap(
      await secureApi.post(
        `${ADMIN_ROOT}/matches/${matchId}/uploads/${photoId}/complete`,
      ),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to process the uploaded photo."));
  }
}

// ---------------------------------------------------------------------------
// Match feature toggles — `MATCH_GALLERY` (guide §20). There is no season-
// level equivalent for this — that flag is `TOURNAMENT_GALLERY`, managed
// via the existing season feature-toggle service.
// ---------------------------------------------------------------------------

export interface MatchFeatureToggle {
  name: string;
  label: string;
  enabled: boolean;
}

export async function listMatchFeatureToggles(
  sport: string,
  matchId: string,
): Promise<MatchFeatureToggle[]> {
  try {
    const data = unwrap<MatchFeatureToggle[]>(
      await secureApi.get(
        `/api/v2/tournaments/multi-sports/manage/${sport}/match/${matchId}/feature-toggles`,
      ),
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load match features."));
  }
}

export async function setMatchFeatureToggle(
  sport: string,
  matchId: string,
  featureName: string,
  enabled: boolean,
): Promise<MatchFeatureToggle[]> {
  try {
    const data = unwrap<MatchFeatureToggle[]>(
      await secureApi.post(
        `/api/v2/tournaments/multi-sports/manage/${sport}/match/${matchId}/feature-toggles/${encodeURIComponent(featureName)}`,
        { enabled },
      ),
    );
    return Array.isArray(data) ? data : [];
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to update the match feature."));
  }
}

// ---------------------------------------------------------------------------
// Monetization products — used to price paid albums (guide §15).
// ---------------------------------------------------------------------------

export interface CreateProductPayload {
  name: string;
  type: "one_time";
  status: "active";
  applies_to: string[];
  target_resource_keys: string[];
  features: string[];
  pricing: Record<string, RegionPrice>;
  visibility: {
    allowed_modules: string[];
    geo_allow_list: string[];
    geo_block_list: string[];
    applies_to_resource_types: string[];
  };
  revenue_split: { platform: number; league: number; academy: number };
}

export interface MonetizationProduct {
  _id?: string;
  id?: string;
  name: string;
  type: string;
  status: string;
  pricing: Record<string, RegionPrice>;
  target_resource_keys?: string[];
}

export async function createProduct(
  payload: CreateProductPayload,
): Promise<MonetizationProduct> {
  try {
    return unwrap(await secureApi.post(`/api/v1/monetization/products`, payload));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to create the product."));
  }
}

export async function updateProduct(
  productId: string,
  payload: Partial<CreateProductPayload>,
): Promise<MonetizationProduct> {
  try {
    return unwrap(
      await secureApi.post(
        `/api/v1/monetization/products/${productId}/update`,
        payload,
      ),
    );
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to update the product."));
  }
}

export async function getProduct(productId: string): Promise<MonetizationProduct> {
  try {
    return unwrap(await secureApi.get(`/api/v1/monetization/products/${productId}`));
  } catch (error) {
    throw new Error(errorMessage(error, "Failed to load the product."));
  }
}

// ---------------------------------------------------------------------------
// Direct-to-S3 upload helper — no admin auth/cookies, only the required
// headers returned by the initiate call (guide §15/§20).
// ---------------------------------------------------------------------------

export async function uploadToPresignedUrl(
  uploadUrl: string,
  requiredHeaders: Record<string, string>,
  file: File,
): Promise<void> {
  const result = await fetch(uploadUrl, {
    method: "PUT",
    headers: requiredHeaders,
    body: file,
  });
  if (!result.ok) {
    throw new Error("The image upload to storage failed.");
  }
}

export const ACCEPTED_GALLERY_MIME_TYPES = [
  "image/jpeg",
  "image/jpg",
  "image/png",
  "image/webp",
  "image/avif",
];

export const MAX_GALLERY_UPLOAD_BYTES = 20 * 1024 * 1024;
